/**
 * Frequency-aware "expected visit" for route scheduling (Slice 12).
 * Weekly behavior matches legacy: service_day === date.getDay(), null service_day → never expected.
 *
 * @param {object} customer — customer row (service_day, service_frequency, biweekly_anchor_date)
 * @param {Date} date — local calendar day to evaluate
 * @returns {boolean}
 */
export function computeExpectedVisit(customer, date) {
  if (!customer || !(date instanceof Date) || Number.isNaN(date.getTime())) return false;

  const { service_day: serviceDay } = customer;
  if (serviceDay === null || serviceDay === undefined) return false;

  const freq = normalizeFrequency(customer.service_frequency);
  if (freq === 'on_demand') return false;

  if (freq === 'twice_weekly') {
    const day = date.getDay();
    return day === serviceDay || (customer.service_day_2 != null && day === customer.service_day_2);
  }

  if (date.getDay() !== serviceDay) return false;
  if (freq === 'weekly') return true;

  if (freq === 'biweekly') {
    const anchor = customer.biweekly_anchor_date;
    if (!anchor || typeof anchor !== 'string') return false;
    return isBiweeklyOnWeek(date, anchor);
  }

  return true;
}

function normalizeFrequency(value) {
  if (value == null || value === '') return 'weekly';
  return value;
}

/** @param {object} customer */
export function isOnDemandCustomer(customer) {
  return normalizeFrequency(customer?.service_frequency) === 'on_demand';
}

/**
 * One-time client (`customers.customer_type === 'one_off'`): no recurring
 * schedule, no pool lifecycle season. The row carries `service_frequency: NULL`
 * and `lifecycle_status: NULL` — the latter enforced structurally by the
 * `customers_one_off_has_no_lifecycle` CHECK (20260918001400), which is what
 * keeps these clients off the Apertura pool tab. Before that migration every
 * creation path stamped `'opening'` on them, which is exactly how they ended
 * up there.
 * @param {object} customer
 */
export function isOneOffCustomer(customer) {
  return customer?.customer_type === 'one_off';
}

/**
 * Belongs in the Demanda (on-demand) pool tab: no recurring schedule at all.
 * A one-off customer's `service_frequency` is NULL, and `normalizeFrequency`
 * turns a null/empty frequency into `'weekly'` — so `isOnDemandCustomer` alone
 * can never match a one-off. This predicate ORs the two so one-offs join the
 * same pool as true on-demand recurring clients, carrying a ÚNICO pill to
 * stay distinguishable.
 * @param {object} customer
 */
export function belongsOnDemandPool(customer) {
  return isOneOffCustomer(customer) || isOnDemandCustomer(customer);
}

/**
 * Sunday-start week alignment: same parity as anchor's week → "on" week.
 * @param {Date} date
 * @param {string} anchorYyyyMmDd — YYYY-MM-DD
 */
export function isBiweeklyOnWeek(date, anchorYyyyMmDd) {
  const anchor = parseLocalDate(anchorYyyyMmDd);
  if (!anchor) return false;

  const startD = weekStartSunday(date);
  const startA = weekStartSunday(anchor);
  const msWeek = 7 * 24 * 60 * 60 * 1000;
  const weeksBetween = Math.round((startD.getTime() - startA.getTime()) / msWeek);
  return Math.abs(weeksBetween) % 2 === 0;
}

function weekStartSunday(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - x.getDay());
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseLocalDate(yyyyMmDd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yyyyMmDd).trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const day = parseInt(m[3], 10);
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
  return d;
}
